/* see docs/EXTRACTED_COMMENTS.md#c062 */

import type { API, InlineTool } from "@editorjs/editorjs"

export type PickerVariant = "swatch" | "list" | "chips"

export interface PickerEntry {
  label: string
  className: string
  /** Inline styles applied to the wrapping span. */
  style: Partial<CSSStyleDeclaration>
  /** Optional swatch colour for the dropdown chip. */
  swatch?: string
  /* see docs/EXTRACTED_COMMENTS.md#c063 */
  clear?: boolean
}

export abstract class InlineClassPicker implements InlineTool {
  static isInline = true

  /** Keep styled (and classed) spans intact through block save/sanitization. */
  static get sanitize() {
    return {
      span: {
        class: true,
        style: true,
      },
    }
  }

  protected api: API
  private actions: HTMLDivElement | null = null
  private savedRange: Range | null = null
  private freeControlEl: HTMLElement | null = null
  // see docs/EXTRACTED_COMMENTS.md#c064
  private liveSpan: HTMLElement | null = null

  constructor({ api }: { api: API }) {
    this.api = api
  }

  /* see docs/EXTRACTED_COMMENTS.md#c065 */
  protected get colorProperty(): "color" | "backgroundColor" | null {
    return null
  }

  /** Tooltip / accessible title. */
  protected abstract get pickerTitle(): string
  /** SVG/markup for the toolbar button icon. */
  protected abstract get icon(): string
  /** The options to show in the popover. */
  protected abstract get entries(): PickerEntry[]

  /** Popover body layout. */
  protected get variant(): PickerVariant {
    return "list"
  }

  /* see docs/EXTRACTED_COMMENTS.md#c066 */
  protected renderFreeControl(): HTMLElement | null {
    return null
  }

  render(): HTMLElement {
    const button = document.createElement("button")
    button.type = "button"
    button.classList.add(this.api.styles.inlineToolButton)
    button.innerHTML = this.icon
    button.title = this.pickerTitle
    return button
  }

  renderActions(): HTMLElement {
    const wrapper = document.createElement("div")
    wrapper.className = `wdc-pop wdc-pop--${this.variant}`
    wrapper.hidden = true

    const freeControl = this.renderFreeControl()
    if (freeControl) {
      this.freeControlEl = freeControl
      wrapper.appendChild(freeControl)
    }

    const body = document.createElement("div")
    body.className = "wdc-pop__body"

    for (const entry of this.entries) {
      body.appendChild(this.renderOption(entry))
    }

    if (body.childElementCount > 0) wrapper.appendChild(body)

    this.actions = wrapper
    return wrapper
  }

  private renderOption(entry: PickerEntry): HTMLElement {
    const option = document.createElement("button")
    option.type = "button"
    option.title = entry.label
    option.addEventListener("click", event => {
      event.preventDefault()
      event.stopPropagation()
      this.applyEntry(entry)
    })

    if (this.variant === "swatch") {
      option.className = entry.clear ? "wdc-swatch wdc-swatch--clear" : "wdc-swatch"
      if (!entry.clear) {
        option.style.setProperty(
          "--swatch",
          entry.swatch || (entry.style.color as string) || "transparent",
        )
      }
      return option
    }

    if (this.variant === "chips") {
      option.className = "wdc-chip"
      option.textContent = entry.label
      return option
    }

    option.className = "wdc-row"
    const preview = document.createElement("span")
    preview.className = "wdc-row__preview"
    Object.assign(preview.style, entry.style)
    preview.textContent = "Ag"

    const label = document.createElement("span")
    label.className = "wdc-row__label"
    label.textContent = entry.label

    option.appendChild(preview)
    option.appendChild(label)
    return option
  }

  /** Called by EditorJS when the toolbar button is activated. */
  surround(range: Range): void {
    if (!range) return
    this.savedRange = range.cloneRange()
    this.liveSpan = null
    if (this.actions) {
      this.actions.hidden = !this.actions.hidden
      // Opening: seed the colour control with the selection's current colour.
      if (!this.actions.hidden && this.colorProperty && this.freeControlEl) {
        const setValue = (this.freeControlEl as { __setValue?: (c: string) => void })
          .__setValue
        const current = this.detectColor(range, this.colorProperty)
        if (setValue && current) setValue(current)
      }
    }
  }

  /* see docs/EXTRACTED_COMMENTS.md#c363 */
  private detectColor(
    range: Range,
    prop: "color" | "backgroundColor",
  ): string | null {
    const node: Node | null = range.startContainer
    let el: HTMLElement | null =
      node && node.nodeType === Node.ELEMENT_NODE
        ? (node as HTMLElement)
        : node?.parentElement ?? null
    if (!el) return null
    if (prop === "color") {
      return window.getComputedStyle(el).color || null
    }
    // see docs/EXTRACTED_COMMENTS.md#c364
    while (el) {
      const bg = window.getComputedStyle(el).backgroundColor
      if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") return bg
      el = el.parentElement
    }
    return null
  }

  checkState(): boolean {
    return false
  }

  clear(): void {
    if (this.actions) this.actions.hidden = true
    this.savedRange = null
  }

  private styleSpan(span: HTMLElement, entry: PickerEntry): void {
    span.removeAttribute("style")
    span.className = ""
    if (entry.className) {
      entry.className
        .split(/\s+/)
        .filter(Boolean)
        .forEach(cls => span.classList.add(cls))
    }
    Object.assign(span.style, entry.style)
  }

  /* see docs/EXTRACTED_COMMENTS.md#c365 */
  protected previewEntry(entry: PickerEntry): void {
    if (this.liveSpan) {
      this.styleSpan(this.liveSpan, entry)
      return
    }
    const range = this.savedRange
    if (!range) return
    const span = document.createElement("span")
    this.styleSpan(span, entry)
    try {
      span.appendChild(range.extractContents())
      range.insertNode(span)
      this.liveSpan = span
      // see docs/EXTRACTED_COMMENTS.md#c366
      const next = document.createRange()
      next.selectNodeContents(span)
      this.savedRange = next
      this.api.selection.expandToTag(span)
    } catch {
      // Selection spanned non-text nodes; bail out gracefully.
    }
  }

  protected applyEntry(entry: PickerEntry): void {
    // see docs/EXTRACTED_COMMENTS.md#c367
    if (this.liveSpan) {
      this.styleSpan(this.liveSpan, entry)
      this.finishApply()
      return
    }

    const range = this.savedRange
    if (!range) return

    const span = document.createElement("span")
    this.styleSpan(span, entry)

    try {
      span.appendChild(range.extractContents())
      range.insertNode(span)
      this.api.selection.expandToTag(span)
    } catch {
      // Selection spanned non-text nodes; bail out gracefully.
    }

    this.finishApply()
  }

  private finishApply(): void {
    if (this.actions) this.actions.hidden = true
    this.savedRange = null
    this.liveSpan = null
    this.api.inlineToolbar.close()
  }
}
